/* ImportScreenComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");

const Renderer = require("./ImportScreenRenderer");
const {
    templateFileName,
    baseFieldsData,
    getFieldsLabels,
    generateCSV,
    parseCSV,
    getListOfHeadersDefinitions,
} = require("./utils");
const {formatCustomFields} = require("../../../deviceexplorer/actions/import/utils");

class ImportScreenComponent extends EventEmitter {
    constructor(importActionComponent, logger, CustomFields, userSession, io) {
        super();
        this._logger = logger;
        this._CustomFieldsEntity = CustomFields;
        this._userSession = userSession;
        this._io = io;
        this._renderer = new Renderer(this);
        this._registerListeners();
        this._renderer.render();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    get logger() {
        return this._logger;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    // prettier-ignore
    set "$teve.ui.context"(context) { //jshint ignore:line
        this._context = context;
    }

    _registerListeners() {}

    _unregisterListeners() {}

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._logger = undefined;
                this._io = undefined;
                this._renderer = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "ImportScreenComponent.shutdown() error.");
            }
        );
    }

    sendImportData(data) {
        this._context.resolve({
            importData: data,
        });
    }

    sendImportError(err) {
        this._context.reject(err);
    }

    downloadCsvTemplate() {
        return this._getCustomFieldsDefs()
            .then((customFieldsData) => {
                const fieldsData = [].concat(baseFieldsData, customFieldsData);

                const columnHeaders = getFieldsLabels(fieldsData);
                return generateCSV([], {columns: columnHeaders});
            })
            .then((templateContent) => {
                this._io.fileFactory.saveStringAsFile(templateContent, templateFileName);
                return Promise.resolve();
            });
    }

    parseData(file, type) {
        new Promise((resolve, reject) => {
            const onloadHandler = (type) => (e) => {
                const data = e.target.result;
                if (type === "csv") {
                    this._parseCSVData(data).then(resolve, reject);
                } else if (type === "json") {
                    this._parseJson(data).then(resolve, reject);
                }
            };

            const onErrorHandler = (error) => {
                reject({
                    errorCode: "FileReadError",
                    errorMsg: "File reading error",
                    errorStack: error,
                });
            };

            if (type === "csv" || type === "json") {
                let fileReader = new FileReader();

                fileReader.onload = onloadHandler(type);
                fileReader.onerror = onErrorHandler;
                fileReader.readAsText(file);
            } else {
                reject({
                    errorCode: "UnknownImportFileType",
                    errorMsg: `Unknown file type used for import: ${type}`,
                });
            }
        })
            .then((data) => this.sendImportData(data))
            .catch((error) => {
                this._logger.warn({error}, "ImportScreenComponent data parse error.");
                this.sendImportError(error);
            });
    }

    cancelAction(err) {
        this._context.reject(err);
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _getCustomFieldsDefs() {
        return this._CustomFieldsEntity
            .loadLabelValues(this._userSession.activeOrganization.id)
            .catch((error) => {
                this._logger.warn({error}, "Can not get Custom Fields definitions");
                return [];
            })
            .then((customFields) =>
                customFields
                    .filter(
                        ({systemOwned, enabled, deviceOnly}) =>
                            !systemOwned && deviceOnly === false && enabled !== false
                    )
                    .sort(this._CustomFieldsEntity.getSortingComparator())
            )
            .then(formatCustomFields);
    }

    _parseJson(data) {
        return new Promise((resolve, reject) => {
            try {
                let parsedJson = JSON.parse(data);
                resolve(_.isArray(parsedJson) ? parsedJson : [parsedJson]);
            } catch (err) {
                if (err.name && err.name === "SyntaxError") {
                    reject({
                        errorCode: "SyntaxError",
                        errorMsg: "Unable to parse JSON file.",
                    });
                } else {
                    reject(err);
                }
            }
        });
    }

    _parseCSVData(fileData) {
        return Promise.all([this._getCustomFieldsDefs(), parseCSV(fileData)])
            .then(([customFieldsData, parsedData]) => {
                const [headers, ...data] = parsedData;

                const fieldsData = [].concat(baseFieldsData, customFieldsData);

                const columnsDefinitions = getListOfHeadersDefinitions(fieldsData, headers);

                const cellsReducer = (site, cellValue, idx) => {
                    const colDef = columnsDefinitions[idx];
                    if (!colDef) {
                        return site;
                    }
                    if (colDef.isCustomField) {
                        return _.set(site, ["customFieldValues", colDef.title], cellValue);
                    }
                    let val;
                    switch (String(colDef.type).toLowerCase()) {
                        case "number":
                            val = numberProcess(cellValue);
                            break;
                        case "boolean":
                            val = booleanProcess(cellValue);
                            break;
                        case "enum":
                            val = enumProcess(cellValue, colDef.typeInfo);
                            break;
                        case "array":
                            // since user enters \n manually, \ needs to be escaped for split to work : \\n
                            val = arrayProcess(cellValue, colDef.arrayDelimiter || "\\n");
                            break;
                        case "string":
                            if (colDef.title === "Name") {
                                val = cellValue.trim();
                                break;
                            }
                            if (colDef.title === "Type" && cellValue.trim() === "") {
                                val = "Site";
                                break;
                            }
                            if (
                                colDef.title ===
                                "Enable site auto-assignment of Devices to this Site"
                            ) {
                                val = cellValue.trim();
                                val = booleanProcess(val);
                            }
                            if (colDef.title === "Auto Assign Device Property") {
                                val = cellValue.trim();
                                const v = val.toLowerCase();
                                if (v === "name" || v === "device name") {
                                    val = "NAME";
                                } else if (v === "hostname" || v === "detected_hostname") {
                                    val = "DETECTED_HOSTNAME";
                                }
                            }
                            if (colDef.title === "Auto-assign Device Hostname RegEx") {
                                let isValid = true;
                                val = "";
                                try {
                                    new RegExp(cellValue.trim());
                                } catch (e) {
                                    isValid = false;
                                }

                                if (isValid) {
                                    val = cellValue.trim();
                                }
                            }
                        /* falls through */
                        default:
                            val = cellValue;
                            break;
                    }
                    return _.set(site, colDef.path, val);
                };

                return data.map((row) => row.reduce(cellsReducer, {}));

                function booleanProcess(value) {
                    return ["true", "yes", "on", "1"].includes(String(value).toLowerCase());
                }
                function numberProcess(value) {
                    return isNaN(value) && value !== null ? null : Number(value);
                }
                function enumProcess(value, typeInfo) {
                    const {ids = [], labels = []} = typeInfo || {};
                    const labelIndex = labels.indexOf(value);
                    return ids[labelIndex] || null;
                }
                function arrayProcess(value, delimiter) {
                    // considering \n as default
                    // expecting the string to have either \n or the delimter prop not both
                    if (value) {
                        if (value.search(delimiter)) {
                            return value.split(delimiter);
                        }
                        return value.split("\n");
                    }
                }
            })
            .catch((error) =>
                Promise.reject({
                    errorCode: "CsvImportError",
                    errorMsg: "Unable to import csv file.",
                    errorStack: error,
                })
            );
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}
module.exports = ImportScreenComponent;
